import { createContext, useContext, useLayoutEffect } from 'react';
import { WizardStep } from '../constants/types';

/** Provided by WizardModal to the active step's content. Internal: step content should use useWizardStep(). */
export interface WizardStepContextValue {
    /** The step currently being shown. */
    step: WizardStep;
    /** Sets (or, with undefined, clears) the content's Save & Continue value for the current step. */
    setEnableSaveAndContinue: (enabled: boolean | undefined) => void;
}

export const WizardStepContext = createContext<WizardStepContextValue | null>(null);

export interface UseWizardStepOptions {
    /**
     * Whether Save & Continue is enabled while this content is shown. Pass the current value each render
     * (e.g. form validity) and the button follows it. Omit it, or don't use this hook, and the button is enabled.
     */
    enableSaveAndContinue?: boolean;
}

export interface UseWizardStepResult {
    /** The step currently being shown. */
    step: WizardStep;
}

/**
 * Connects a step's content to its WizardModal. Only for behavior of the open step (e.g. Save & Continue);
 * per-step info that must be known for every step (e.g. progressBarInfo) belongs on the WizardStep itself.
 *
 * @example
 * useWizardStep({ enableSaveAndContinue: isValid });
 */
export const useWizardStep = ({ enableSaveAndContinue }: UseWizardStepOptions = {}): UseWizardStepResult => {
    const context = useContext(WizardStepContext);
    if (!context) {
        throw new Error('useWizardStep must be used inside a WizardModal step.');
    }

    const { step, setEnableSaveAndContinue } = context;

    // Layout effect so the value is applied before the browser paints: no flash of an enabled button.
    // Cleared again when this content unmounts, so it can't outlive the content that set it.
    useLayoutEffect(() => {
        setEnableSaveAndContinue(enableSaveAndContinue);
        return () => setEnableSaveAndContinue(undefined);
    }, [enableSaveAndContinue, setEnableSaveAndContinue]);

    return { step };
};
